import logger from '@/utils/logger';
/**
 * COMPOSANT: SUBSCRIPTION MANAGER
 * Gestion complète de l'abonnement utilisateur
 * - Affichage plan actuel
 * - Upgrade / Downgrade
 * - Annulation
 * - Historique paiements
 */

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../auth/AuthProvider';
import { useTokens } from '../../hooks/useTokens';
import { supabase } from '../../lib/supabase';
import { 
  CreditCard, 
  Calendar, 
  AlertCircle, 
  CheckCircle, 
  ArrowUpCircle, 
  ArrowDownCircle,
  XCircle,
  Loader2,
  Receipt
} from 'lucide-react';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3000';

interface Subscription {
  id: string;
  plan_name: string;
  status: string;
  stripe_subscription_id: string;
  current_period_start: string;
  current_period_end: string;
  cancel_at_period_end: boolean;
}

interface PaymentHistory {
  id: string;
  amount: number;
  currency: string;
  status: string;
  created_at: string;
  description?: string;
}

export default function SubscriptionManager() {
  const { user } = useAuth();
  const { subscriptionTier, monthlyAllowance, resetAt } = useTokens();
  const navigate = useNavigate();

  const [subscription, setSubscription] = useState<Subscription | null>(null);
  const [paymentHistory, setPaymentHistory] = useState<PaymentHistory[]>([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);

  useEffect(() => {
    if (user) {
      loadSubscriptionData();
    }
  }, [user]);

  const loadSubscriptionData = async () => {
    if (!user) {
      logger.warn('[SubscriptionManager] Pas d\'utilisateur');
      setLoading(false);
      return;
    }


    try {
      setLoading(true);

      // Récupérer le token d'authentification
      const { data: { session }, error: sessionError } = await supabase.auth.getSession();
      
      if (sessionError || !session) {
        logger.error('[SubscriptionManager] Session invalide:', sessionError);
        setLoading(false);
        return;
      }


      // Charger l'abonnement actif
      const subResponse = await fetch(`${API_URL}/api/billing/subscription`, {
        headers: {
          'Authorization': `Bearer ${session.access_token}`,
        },
      });


      if (subResponse.ok) {
        const subData = await subResponse.json();
        setSubscription(subData);
      } else {
        const errorText = await subResponse.text();
        logger.error('[SubscriptionManager] Erreur subscription:', subResponse.status, errorText);
      }

      // Charger l'historique des paiements
      const historyResponse = await fetch(`${API_URL}/api/billing/payment-history`, {
        headers: {
          'Authorization': `Bearer ${session.access_token}`,
        },
      });


      if (historyResponse.ok) {
        const historyData = await historyResponse.json();
        setPaymentHistory(historyData.payments || []);
      } else {
        const errorText = await historyResponse.text();
        logger.error('[SubscriptionManager] Erreur payment history:', historyResponse.status, errorText);
      }

    } catch (error) {
      logger.error('[SubscriptionManager] Exception:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCancelSubscription = async () => {
    if (!subscription || !window.confirm(
      `Êtes-vous sûr de vouloir annuler votre abonnement ${(subscription.plan_name || 'PRO').toUpperCase()} ?\n\n` +
      `Votre accès restera actif jusqu'au ${new Date(subscription.current_period_end).toLocaleDateString('fr-FR')}.`
    )) {
      return;
    }

    try {
      setActionLoading(true);

      // Récupérer le token d'authentification
      const { data: { session }, error: sessionError } = await supabase.auth.getSession();
      
      if (sessionError || !session) {
        alert('Session expirée. Veuillez vous reconnecter.');
        return;
      }

      const response = await fetch(`${API_URL}/api/billing/subscription/cancel`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session.access_token}`,
        },
        body: JSON.stringify({
          subscription_id: subscription.stripe_subscription_id,
        }),
      });

      if (response.ok) {
        alert('Votre abonnement sera annulé à la fin de la période en cours.');
        loadSubscriptionData();
      } else {
        const error = await response.json();
        alert(`Erreur: ${error.error || 'Impossible d\'annuler l\'abonnement'}`);
      }
    } catch (error) {
      logger.error('[SubscriptionManager] Erreur annulation:', error);
      alert('Une erreur est survenue. Veuillez réessayer.');
    } finally {
      setActionLoading(false);
    }
  };

  const handleUpgrade = () => {
    navigate('/billing');
  };

  const getPlanLabel = (tier: string) => {
    const labels: Record<string, string> = {
      free: 'Free',
      pro: 'Pro',
      business: 'Business',
      enterprise: 'Enterprise',
    };
    return labels[tier] || tier;
  };

  const getPlanColor = (tier: string) => {
    const colors: Record<string, string> = {
      free: 'text-purple-700 dark:text-purple-400 bg-purple-100 dark:bg-purple-600/20 border-purple-300 dark:border-purple-400/30',
      pro: 'text-green-400 bg-green-500/10 border-green-500/30',
      business: 'text-purple-400 bg-purple-500/10 border-purple-500/30',
      enterprise: 'text-indigo-700 dark:text-indigo-400 bg-indigo-100 dark:bg-indigo-500/20 border-indigo-300 dark:border-indigo-400/30',
    };
    return colors[tier] || colors.free;
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'active':
        return <CheckCircle className="w-5 h-5 text-green-400" />;
      case 'canceled':
      case 'cancelled':
        return <XCircle className="w-5 h-5 text-red-400" />;
      case 'past_due':
        return <AlertCircle className="w-5 h-5 text-purple-600 dark:text-purple-400" />;
      default:
        return <AlertCircle className="w-5 h-5 text-theme-secondary" />;
    }
  };

  const formatAmount = (amount: number, currency: string) => {
    return new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency: currency.toUpperCase(),
    }).format(amount / 100);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-purple-600 dark:text-purple-400" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Plan actuel */}
      <div className="p-6 rounded-2xl bg-gradient-to-br bg-theme-surface border border-theme-border">
        <h3 className="text-lg font-semibold text-theme-primary mb-4 flex items-center gap-2">
          <CreditCard className="w-5 h-5" />
          Abonnement actuel
        </h3>

        <div className="flex items-center justify-between">
          <div>
            <div className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg border ${getPlanColor(subscriptionTier)}`}>
              <span className="text-2xl font-bold uppercase">
                {getPlanLabel(subscriptionTier)}
              </span>
              {subscription && getStatusIcon(subscription.status)}
            </div>

            {subscription && subscription.current_period_end && (
              <div className="mt-4 space-y-2 text-sm text-theme-secondary">
                <div className="flex items-center gap-2">
                  <Calendar className="w-4 h-4" />
                  <span>
                    Période en cours jusqu'au{' '}
                    {new Date(subscription.current_period_end).toLocaleDateString('fr-FR', {
                      day: 'numeric',
                      month: 'long',
                      year: 'numeric',
                    })}
                  </span>
                </div>
                {subscription.cancel_at_period_end && (
                  <div className="flex items-center gap-2 text-purple-600 dark:text-purple-400">
                    <AlertCircle className="w-4 h-4" />
                    <span>Abonnement annulé - Actif jusqu'à la fin de période</span>
                  </div>
                )}
              </div>
            )}
            
            {/* Si pas de subscription Stripe mais plan actif, afficher l'info depuis le profil */}
            {!subscription && resetAt && (
              <div className="mt-4 space-y-2 text-sm text-theme-secondary">
                <div className="flex items-center gap-2">
                  <Calendar className="w-4 h-4" />
                  <span>
                    Prochain renouvellement le{' '}
                    {new Date(resetAt).toLocaleDateString('fr-FR', {
                      day: 'numeric',
                      month: 'long',
                      year: 'numeric',
                    })}
                  </span>
                </div>
              </div>
            )}

            <div className="mt-4 text-sm text-theme-tertiary">
              <span className="font-medium text-theme-primary">
                {monthlyAllowance?.toLocaleString('fr-FR')} tokens
              </span>
              {' '}par mois
            </div>
          </div>

          <div className="flex flex-col gap-2">
            {subscriptionTier !== 'enterprise' && (
              <button
                onClick={handleUpgrade}
                className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 text-theme-primary font-medium rounded-lg transition-all"
              >
                <ArrowUpCircle className="w-4 h-4" />
                Upgrade
              </button>
            )}

            {subscription && !subscription.cancel_at_period_end && subscriptionTier !== 'free' && (
              <button
                onClick={handleCancelSubscription}
                disabled={actionLoading}
                className="flex items-center gap-2 px-4 py-2 bg-theme-hover hover:bg-red-900/50 text-red-400 font-medium rounded-lg transition-all disabled:opacity-50"
              >
                {actionLoading ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <XCircle className="w-4 h-4" />
                )}
                Annuler
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Historique des paiements */}
      {paymentHistory.length > 0 && (
        <div className="p-6 rounded-2xl bg-gradient-to-br bg-theme-surface border border-theme-border">
          <h3 className="text-lg font-semibold text-theme-primary mb-4 flex items-center gap-2">
            <Receipt className="w-5 h-5" />
            Historique des paiements
          </h3>

          <div className="space-y-3">
            {paymentHistory.map((payment) => (
              <div
                key={payment.id}
                className="flex items-center justify-between p-4 rounded-lg bg-theme-hover/50 border border-theme-border/50"
              >
                <div>
                  <div className="font-medium text-theme-primary">
                    {formatAmount(payment.amount, payment.currency)}
                  </div>
                  <div className="text-sm text-theme-secondary">
                    {new Date(payment.created_at).toLocaleDateString('fr-FR', {
                      day: 'numeric',
                      month: 'long',
                      year: 'numeric',
                    })}
                  </div>
                  {payment.description && (
                    <div className="text-xs text-theme-tertiary mt-1">{payment.description}</div>
                  )}
                </div>

                <div>
                  {payment.status === 'succeeded' && (
                    <span className="px-3 py-1 bg-green-500/20 text-green-400 text-xs font-medium rounded-full border border-green-500/30">
                      Payé
                    </span>
                  )}
                  {payment.status === 'pending' && (
                    <span className="px-3 py-1 bg-purple-500/20 text-purple-600 dark:text-purple-400 text-xs font-medium rounded-full border border-purple-500/30">
                      En attente
                    </span>
                  )}
                  {payment.status === 'failed' && (
                    <span className="px-3 py-1 bg-red-500/20 text-red-400 text-xs font-medium rounded-full border border-red-500/30">
                      Échoué
                    </span>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Message si plan free */}
      {subscriptionTier === 'free' && (
        <div className="p-6 rounded-2xl bg-gradient-to-r from-purple-600/10 dark:from-purple-600/20 to-purple-500/10 border border-purple-600/20 dark:border-purple-500/30">
          <h4 className="text-theme-primary font-semibold mb-2">Passez à un plan payant</h4>
          <p className="text-theme-secondary text-sm mb-4">
            Débloquez plus de tokens, des analyses avancées et des fonctionnalités premium.
          </p>
          <button
            onClick={handleUpgrade}
            className="px-6 py-3 bg-gradient-to-r from-purple-600 via-indigo-600 to-blue-600 dark:from-purple-500 dark:via-indigo-500 dark:to-blue-500 hover:scale-105 text-theme-primary font-medium rounded-lg transition-all"
          >
            Voir les plans
          </button>
        </div>
      )}
    </div>
  );
}

